import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { DisclaimerComponent } from "../core/components/disclaimer/disclaimer.component";
import { Disclaimer } from "../core/components/disclaimer/disclaimer.model";
import { ActivatedRoute, Router } from "@angular/router";

import { LocalStorageUtil } from "../shared/utils/local-storage.util";
import { UserAppService } from "../core/services/userapp.service";
import {
  ToasterService,
  MessageType,
  Decision,
  ServiceConstant,
  PersistenceAction,
} from "../../../../shared-lib/src/public-api";
import { DataStoreService } from "../core/services/data-store.service";
import { combineLatest } from "rxjs";

import { environment } from "../../environments/environment";
import { SwPush } from "@angular/service-worker";
import { NotificationSubsUtil } from "../shared/utils/notification-subs.util";
import { SubscriptionService } from "../core/services/subscription.service";
import { SelectLangModal } from "../shared/components/select-lang-modal/select-lang-modal.component";
import { QuestionListenerUtil } from "../shared/utils/question-listener.util";
import { UsageData } from "../shared/model/usage-data.model";

@Component({
  selector: "app-mobile-guidelines",
  templateUrl: "./guidelines.component.html",
  styleUrls: ["./guidelines.component.css"],
})
export class GuidelinesComponent implements OnInit {

  public decisions: Decision[] = [];
  public fullData$;
  public userTypeName: string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public dataStoreService: DataStoreService,
    public decisionService: UserAppService,
    public translate: TranslateService,
    public dialog: MatDialog,
    private toasterService: ToasterService,
    private subscriptionService: SubscriptionService,
    private localStorageUtil: LocalStorageUtil,
    public questionListenerUtil: QuestionListenerUtil,
    private notificationSubsUtil: NotificationSubsUtil,
    private swPush: SwPush
  ) {
    this.onError = this.onError.bind(this);
    this.onSubscriptionSuccess = this.onSubscriptionSuccess.bind(this);
    this.subscribeToNotifications = this.subscribeToNotifications.bind(this);
    this.isSubscribedToPush = this.isSubscribedToPush.bind(this);
    this.setupGuidelines = this.setupGuidelines.bind(this);
    //this.saveStartTime = this.saveStartTime.bind(this);
  }

  private onError(error) {
    this.toasterService.show(MessageType.SYSTEM_ERROR, error);
  }

  private setupGuidelines([params, guidelines]) {
    const hasCategory = params["category"] ? true : false;
    const hasInstId = params["institutionId"] ? true : false;
    if (hasCategory || hasInstId) {
      this.localStorageUtil.saveGuidelinesParams(params["category"],params);
    }
    const usage = this.localStorageUtil.getUsageInfo(params["category"]);
    if (usage) {
      usage.categoryName = hasCategory ? params["category"].toLowerCase() : "";
      usage.institutionId = hasInstId ? +params["institutionId"] : 0;
      this.localStorageUtil.saveUsageInfo(params["category"],usage);

    }
    guidelines = this.decisionService.filterGuidelines(params, guidelines);
    this.decisionService.applySurveyVariables(this.userTypeName,guidelines);
    //this functionality is temporarily disabled
    this.isSubscribedToPush();
  }

  private onSubscriptionSuccess(response) {
    if (response.isInError) {
      if (response.messages.length > 0) {
        this.toasterService.show(
          response.messages[0].type,
          response.messages[0].text
        );
      } else {
        this.onError(ServiceConstant.somethingWentWrongMessage);
      }
    }
  }

  private subscribeToNotifications(pushSub: PushSubscription) {
    const pushSubStr = JSON.stringify(pushSub);
    const { endpoint, keys } = JSON.parse(pushSubStr);
    
    const dialogRef = this.dialog.open(SelectLangModal);
    dialogRef.afterClosed().subscribe((langId) => {
      const notificationSub = (langId) ? this.notificationSubsUtil.toSave(
        endpoint,
        keys.p256dh,
        keys.auth,
        this.userTypeName,
        langId
      ) : this.notificationSubsUtil.toSave(
        endpoint,
        keys.p256dh,
        keys.auth,
        this.userTypeName,
        1
      );
      
      this.subscriptionService
        .saveSubscription(notificationSub)
        .subscribe(this.onSubscriptionSuccess, this.onError);
    }); 
  }

  private isSubscribedToPush() {
    const options = { serverPublicKey: environment.vapidPublicKey };
    this.swPush.subscription.subscribe((pushSub: PushSubscription) => {
      if (pushSub === null) {
        this.swPush
          .requestSubscription(options)
          .then(this.subscribeToNotifications)
          .catch(
            err => {
              console.log("Notifications error")
              console.log(err)
            } );
      }
    }, err => {
      console.log("error")
      console.log(err)
    });
  }

  private showSelectedGuideline() {
    const queryParams: any = { lang: this.translate.currentLang };
    if(this.activatedRoute.snapshot.queryParams && this.activatedRoute.snapshot.queryParams.deviceToken){
        queryParams.deviceToken = this.activatedRoute.snapshot.queryParams.deviceToken;
    }
    if (this.decisionService.currentDecision.pathways.length > 1) {
      this.router.navigate(["/guidelines/pathways"], { queryParams });
    } else if (this.decisionService.currentDecision.pathways.length === 1) {
      this.decisionService.selectPathway = this.decisionService.currentDecision.pathways[0];
      //this.saveStartTime();
      this.router.navigate(["guidelines/questiongroup"], { queryParams });
    }
  }

  private showDisclaimerModal(disclaimer: string) {
    const dialogRef = this.dialog.open(DisclaimerComponent, {
      data: new Disclaimer(disclaimer),
    });

    dialogRef.afterClosed().subscribe((result) => {
    if (result) {
      this.localStorageUtil.saveAcceptedDisclaimer(this.userTypeName,true);
      this.showSelectedGuideline();
    } else {
        this.localStorageUtil.saveAcceptedDisclaimer(this.userTypeName,false);
    }
  });
  }

  showDisclaimer(decision: Decision) {
    this.decisionService.selectDecision = decision;
    const pathways = decision.pathways;
    if (pathways === undefined || pathways.length === 0) {
      this.toasterService.show(MessageType.INFORMATIONAL, "Coming soon");
      return;
    }
    const isAcceptedDisclaimer = this.localStorageUtil.getAcceptedDisclaimer(this.userTypeName);
    if (isAcceptedDisclaimer) {
      this.showSelectedGuideline();
    } else {
      this.showDisclaimerModal(decision.disclaimer);
    }
  }

  ngOnInit() {
    this.userTypeName = this.activatedRoute.snapshot.params["category"];
    this.decisionService.selectDecision = null;
    this.decisionService.selectPathway = null;
    this.fullData$ = combineLatest([
      this.activatedRoute.params,
      this.dataStoreService.currentGuidelines$,
    ]).subscribe(this.setupGuidelines);
    this.decisionService.saveStartTime(this.userTypeName);
    this.decisionService.saveUsageInformationOnModelAction(this.userTypeName, PersistenceAction.INSERT, true);
    this.decisionService.sendPreviousUsageData();
    //this.decisionService.requestLocationAccess(this.userTypeName);
    this.setiOSNotificationToken();
  }

  setiOSNotificationToken() {
    
      this.activatedRoute.queryParams.subscribe(params => {
          // Call save ios token
          if(params["deviceToken"]) {
              var sub = this.notificationSubsUtil.toSave("", "", params["deviceToken"], this.activatedRoute.snapshot.params["category"], 2 );
              if(!sub.surverUserId) return;
              this.subscriptionService.saveiOSSubscription(sub).subscribe();

          }
      });
  }

  isDisabled(decision: Decision) {
    if (decision.pathways.length === 0) {
      return true;
    } else {
      return false;
    }
  }
}
